// src/pages/PaymentUpload.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../../utils/config";

const ENDPOINT_BASE = `${API_BASE_URL}/api/paymentsUpload`; // /preview and /commit live here
const shortCustomerName = (s) => String(s || "").split(",")[0].trim();

export default function PaymentUpload() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // form overrides (all optional)
  const [form, setForm] = useState({
    customer_id: "",
    amount: "",
    payment_date: "",
    payment_mode: "",
    reference_no: "",
    voucher_no: "",
    remarks: "",
    invoice_id: "",
  });

  const token = useMemo(() => localStorage.getItem("token"), []);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/customers/rahultemp`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCustomers(res.data || []);
      } catch (err) {
        console.error("Error fetching customers", err);
      }
    };
    fetchCustomers();
  }, [token]);

  // Try to auto-select customer by exact name match from preview
  useEffect(() => {
    if (!preview?.parties?.customer_name || !customers.length) return;
  const name = shortCustomerName(preview.parties.customer_name).toLowerCase();
    const match = customers.find(
    (c) => String(c.name).trim().toLowerCase() === name
  );
  if (match && !form.customer_id) {
    setForm((f) => ({ ...f, customer_id: String(match.id) }));
  }
}, [preview, customers]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileChange = (e) => {
    const uploaded = e.target.files?.[0];
    if (!uploaded) return;
    if (
      uploaded.type !== "application/json" &&
      !/\.json$/i.test(uploaded.name)
    ) {
      setError("Please upload a JSON file (parsed payments JSON).");
      setFile(null);
      return;
    }
    setError("");
    setFile(uploaded);
    setPreview(null);
  };

  const handlePreview = async () => {
    if (!file) return alert("Select a JSON file first");
    setLoading(true);
    setError("");

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axios.post(`${ENDPOINT_BASE}/preview`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      setPreview(res.data || null);

      // Pre-fill form overrides from server commit_preview
      const cp = res.data?.commit_preview || {};
      setForm((prev) => ({
        ...prev,
        amount: cp.amount ?? "",
        payment_date: cp.payment_date ?? "",
        payment_mode: cp.payment_mode ?? "",
        reference_no: cp.reference_no ?? "",
        voucher_no: cp.voucher_no ?? "",

        remarks: cp.remarks ?? "",
        // invoice_id/customer_id kept for user selection
      }));
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  const handleCommit = async () => {
    if (!file) return alert("No file selected");
    setLoading(true);
    setError("");

    const formData = new FormData();
    formData.append("file", file);

    // Append overrides only if provided (avoid clobbering parsed values unnecessarily)
    const appendIf = (key, val) => {
      if (val !== undefined && val !== null && String(val).trim() !== "") {
        formData.append(key, String(val));
      }
    };

    appendIf("customer_id", form.customer_id);
    appendIf("invoice_id", form.invoice_id);
    appendIf("amount", form.amount);
    appendIf("payment_date", form.payment_date);
    appendIf("payment_mode", form.payment_mode);
    appendIf("reference_no", form.reference_no);
    appendIf("voucher_no", form.voucher_no);
    appendIf("remarks", form.remarks);

    try {
      const res = await axios.post(`${ENDPOINT_BASE}/commit`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      const msg = res.data?.status === "duplicate"
        ? `Duplicate skipped (payment #${res.data.payment_id}).`
        : `Committed ✓ (payment #${res.data.payment_id})`;
      alert(msg);

      // reset
      setPreview(null);
      setFile(null);
      setForm({
        customer_id: "",
        amount: "",
        payment_date: "",
        payment_mode: "",
        reference_no: "",
        voucher_no: "",
        remarks: "",
        invoice_id: "",
      });
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || "Commit failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto bg-white shadow rounded">
      <h2 className="text-xl font-bold mb-4">Upload Payment (JSON)</h2>

      <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
        <input
          type="file"
          accept=".json,application/json"
          onChange={handleFileChange}
          className="block"
        />
        <button
          onClick={handlePreview}
          disabled={!file || loading}
          className="bg-blue-600 text-white px-4 py-2 rounded"
        >
          {loading ? "Processing..." : "Preview Payment"}
        </button>
      </div>

      {error && <p className="text-red-600 mt-2">{error}</p>}

      {preview && (
        <>
          {/* Header summary */}
          <div className="mt-6 grid md:grid-cols-3 gap-4 text-sm">
            <div className="border rounded p-3">
              <div className="font-semibold mb-1">Payment</div>
              <div>
                <b>Voucher No:</b> {preview.header?.voucher_no || "-"}
              </div>
              <div>
                <b>Date:</b> {preview.header?.payment_date || "-"}
              </div>
              <div>
                <b>Mode:</b> {preview.header?.payment_mode || "-"}
              </div>
              <div>
                <b>Ref No:</b> {preview.header?.reference_no || "-"}
              </div>
              <div>
                <b>Amount:</b>{" "}
                {preview.header?.amount != null ? preview.header.amount : "-"}
              </div>
            </div>

            <div className="border rounded p-3">
              <div className="font-semibold mb-1">Parties</div>
              <div>
                <b>Customer:</b> {preview.parties?.customer_name || "-"}
              </div>
              <div>
                <b>Ledger (Dr):</b> {preview.parties?.ledger || "-"}
              </div>
            </div>

            <div className="border rounded p-3">
              <div className="font-semibold mb-1">Checks</div>
              <div className={preview.checks?.ok ? "text-green-700" : "text-yellow-800"}>
                {preview.checks?.ok ? "Looks good" : "Needs attention"}
              </div>
              {!!preview.checks?.issues?.length && (
                <ul className="list-disc ml-5 mt-1">
                  {preview.checks.issues.map((it, i) => (
                    <li key={i}>{it}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Remarks */}
          <div className="mt-4 border rounded p-3 text-sm">
            <div className="font-semibold mb-1">Remarks (from JSON)</div>
            <div className="whitespace-pre-wrap">
              {preview.remarks || <span className="text-gray-500">—</span>}
            </div>
          </div>

          {/* Commit form (overrides) */}
          <div className="mt-6 bg-gray-50 p-4 rounded shadow-sm">
            <h3 className="font-medium mb-3">Commit Overrides (optional)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Customer selector */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Customer
                </label>
                <select
                  value={form.customer_id}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, customer_id: e.target.value }))
                  }
                  className="border px-3 py-2 rounded w-full"
                >
                  <option value="">-- Auto (by name) / Select --</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {preview?.parties?.customer_name && (
                  <div className="text-xs text-gray-500 mt-1">
                    Parsed name: <b>{shortCustomerName(preview.parties.customer_name)}</b>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Voucher No
                </label>
                <input
                  type="number"
                  value={form.voucher_no}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, voucher_no: e.target.value }))
                  }
                  className="border px-3 py-2 rounded w-full"
                  placeholder={String(preview.header?.voucher_no ?? "")}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Amount (override)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, amount: e.target.value }))
                  }
                  className="border px-3 py-2 rounded w-full"
                  placeholder={String(preview.header?.amount ?? "")}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Payment Date (override)
                </label>
                <input
                  type="date"
                  value={form.payment_date}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, payment_date: e.target.value }))
                  }
                  className="border px-3 py-2 rounded w-full"
                  placeholder={String(preview.header?.payment_date ?? "")}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Payment Mode (override)
                </label>
                <select
                  value={form.payment_mode}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, payment_mode: e.target.value }))
                  }
                  className="border px-3 py-2 rounded w-full"
                >
                  <option value="">-- Select --</option>
                  <option value="cash">Cash</option>
                  <option value="cheque">Cheque</option>
                  <option value="online">Online</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Reference No (override)
                </label>
                <input
                  type="text"
                  value={form.reference_no}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, reference_no: e.target.value }))
                  }
                  className="border px-3 py-2 rounded w-full"
                  placeholder={String(preview.header?.reference_no ?? "")}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">
                  Remarks (override)
                </label>
                <textarea
                  rows={3}
                  value={form.remarks}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, remarks: e.target.value }))
                  }
                  className="border px-3 py-2 rounded w-full"
                  placeholder={preview.remarks || "—"}
                />
              </div>
            </div>

            <button
              onClick={handleCommit}
              className="bg-green-600 text-white px-4 py-2 rounded mt-4"
              disabled={loading}
            >
              {loading ? "Saving..." : "Commit to DB"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
